import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
	$addUpdateTag,
	$getRoot,
	$getSelection,
	$isElementNode,
	$isRangeSelection,
	$isTextNode,
	BLUR_COMMAND,
	COMMAND_PRIORITY_CRITICAL,
	HISTORY_PUSH_TAG,
	KEY_ARROW_DOWN_COMMAND,
	KEY_ARROW_UP_COMMAND,
	KEY_ENTER_COMMAND,
	KEY_ESCAPE_COMMAND,
	KEY_TAB_COMMAND,
} from 'lexical';

import { editFor, PROVIDERS, suggestionAt } from './formulaSuggest';

/**
 * The controller that ties the suggestion providers (formulaSuggest.js) to a
 * Lexical editor (KPR-332). It owns WHEN the list is open and WHAT a key does
 * to it; the providers own what is offered and the popup (FormulaSuggestions)
 * owns how it looks. FormulaInput mounts it; nothing else knows the editor.
 *
 * WHEN IT OPENS. Only when the person EDITS (a typed letter, a deletion, an
 * insert from the function picker) and a provider has a match at the caret. A
 * caret move (a click, an arrow), an undo or redo, a paste, a value written
 * from outside and a composition in progress never open it, and any of them
 * closes an open list that no longer matches — a list that appears because
 * the caret happened to land beside a word is intrusive, and free typing is
 * allowed to be wrong.
 *
 * WHAT THE KEYS DO, only while the list is open (otherwise every key is the
 * editor's, untouched): Up/Down move through the rows and wrap; Enter and Tab
 * accept; Escape closes (and is not passed on, so a modal around the editor
 * stays open); Shift+Tab and every other key carry on as usual.
 *
 * ACCEPTING is ONE editor update tagged history-push: one undo step that
 * brings back exactly what was typed. An accept that asks to `reopen`
 * (KPR-350: a function's parens, an argument followed by the next one's
 * comma) is also tagged REOPEN_TAG, and the list is worked out again at the
 * new caret, so the next argument's picker opens with no keystroke.
 */

/** Tags an accept after which the list is worked out again (the next argument's picker). */
export const REOPEN_TAG = 'keepr-formula-suggest-reopen';

/** The formula text and where the (collapsed) caret is in it; null with no caret. */
function $textAndCaret(readFormula) {
	const selection = $getSelection();
	if (!$isRangeSelection(selection) || !selection.isCollapsed()) return null;
	const text = readFormula();
	const point = selection.anchor;
	const root = $getRoot();
	const blocks = root.getChildren();
	let offset = 0;
	let found = -1;
	blocks.forEach((block, index) => {
		if (index > 0) offset += 1;
		if (found >= 0) return;
		const children = $isElementNode(block) ? block.getChildren() : [block];
		if (point.type === 'element' && point.key === block.getKey()) {
			let before = 0;
			for (let i = 0; i < point.offset && i < children.length; i += 1) before += children[i].getTextContent().length;
			found = offset + before;
			return;
		}
		for (const child of children) {
			if (point.type === 'text' && child.getKey() === point.key) { found = offset + point.offset; return; }
			offset += child.getTextContent().length;
		}
	});
	if (found < 0 && point.type === 'element' && point.key === root.getKey()) {
		found = 0;
		for (let i = 0; i < point.offset && i < blocks.length; i += 1) found += blocks[i].getTextContent().length + (i > 0 ? 1 : 0);
	}
	return found < 0 ? null : { text, caret: found };
}

/** The editor point (a text node and offset, or the paragraph) that a formula offset names. */
function $pointAt(target) {
	let offset = 0;
	const blocks = $getRoot().getChildren();
	for (let b = 0; b < blocks.length; b += 1) {
		const block = blocks[b];
		if (b > 0) offset += 1;
		if (!$isElementNode(block)) continue;
		const children = block.getChildren();
		for (let i = 0; i < children.length; i += 1) {
			const child = children[i];
			const size = child.getTextContent().length;
			if ($isTextNode(child) && target >= offset && target <= offset + size) return { node: child, offset: target - offset };
			if (target === offset) return { node: block, offset: i };
			offset += size;
		}
		if (target === offset) return { node: block, offset: children.length };
	}
	return null;
}

/** Moves a collapsed caret `back` characters toward the start, inside its text node. */
function $caretBack(back) {
	const after = $getSelection();
	if (!(back > 0) || !$isRangeSelection(after) || !after.isCollapsed() || after.anchor.type !== 'text') return;
	const node = after.anchor.getNode();
	const at = Math.max(0, after.anchor.offset - back);
	after.setTextNodeRange(node, at, node, at);
}

/**
 * `readFormula` is the editor's `$readFormula`, `createPill` its
 * `$createFormulaRefNode` and `createChoice` its `$createFormulaChoiceNode`
 * (passed in, not imported, because FormulaInput mounts this hook and the two
 * files must not import each other); `quietTags` are the updates that are not
 * the person editing (a value written from outside, an undo or redo, a paste).
 */
export default function useFormulaSuggestions({ editor, enabled, tokens, readFormula, createPill, createChoice, quietTags = [], providers = PROVIDERS }) {
	const baseId = useId().replace(/:/g, '');
	const listId = `formula-suggest-${baseId}`;
	const optionId = index => `${listId}-option-${index}`;

	// `current` is what the commands read; the state is only for drawing.
	const [shown, setShown] = useState(null);
	const currentRef = useRef(null);
	const enabledRef = useRef(enabled);
	enabledRef.current = enabled;
	const tokensRef = useRef(tokens);
	tokensRef.current = tokens;
	const readRef = useRef(readFormula);
	readRef.current = readFormula;
	const createPillRef = useRef(createPill);
	createPillRef.current = createPill;
	const createChoiceRef = useRef(createChoice);
	createChoiceRef.current = createChoice;
	const quietRef = useRef(quietTags);
	quietRef.current = quietTags;
	const providersRef = useRef(providers);
	providersRef.current = providers;

	const publish = useCallback(next => {
		currentRef.current = next;
		setShown(next);
	}, []);
	const close = useCallback(() => {
		if (currentRef.current) publish(null);
	}, [publish]);

	const setActive = useCallback(active => {
		const current = currentRef.current;
		if (!current) return;
		publish({ ...current, active });
	}, [publish]);

	const accept = useCallback(index => {
		const current = currentRef.current;
		if (!current || !enabledRef.current) return;
		const item = current.items[index];
		if (!item) return;
		publish(null);
		editor.update(() => {
			const edit = editFor(current, item, current.text);
			const start = $pointAt(edit.start);
			const end = $pointAt(edit.end);
			if (!start || !end) return;
			const selection = $getSelection();
			if (!$isRangeSelection(selection)) return;
			if ($isTextNode(start.node)) selection.anchor.set(start.node.getKey(), start.offset, 'text');
			else selection.anchor.set(start.node.getKey(), start.offset, 'element');
			if ($isTextNode(end.node)) selection.focus.set(end.node.getKey(), end.offset, 'text');
			else selection.focus.set(end.node.getKey(), end.offset, 'element');
			if (edit.reopen) $addUpdateTag(REOPEN_TAG);
			// A pill replaces the span; the caret lands JUST after it. (`selectNext()`
			// with no offsets selects the END of a following text node, which put
			// the caret — and the next argument's comma — after the call's `)`.)
			const insertPill = pill => {
				const current = $getSelection();
				if (!$isRangeSelection(current)) return;
				current.insertNodes([pill]);
				pill.selectNext(0, 0);
			};
			const parts = Array.isArray(edit.parts) ? edit.parts : [edit.token ? { token: edit.token } : { text: edit.text }];
			parts.forEach(part => {
				if (part.token) insertPill(createPillRef.current(part.token));
				else if (part.choice && typeof createChoiceRef.current === 'function') insertPill(createChoiceRef.current(part.choice.text, part.choice.label));
				else if (part.choice) {
					const current = $getSelection();
					if ($isRangeSelection(current)) current.insertRawText(part.choice.text);
				} else if (typeof part.text === 'string') {
					const current = $getSelection();
					if (!$isRangeSelection(current)) return;
					// Replacing the span with nothing still has to remove it.
					if (part.text) current.insertRawText(part.text);
					else current.removeText();
				}
			});
			$caretBack(edit.caretBack);
		}, { discrete: true, tag: HISTORY_PUSH_TAG });
		// The editor keeps focus the whole time (the popup never takes it).
	}, [editor, publish]);

	// Recompute on every editor update.
	useEffect(() => {
		if (!enabled) { close(); return undefined; }
		return editor.registerUpdateListener(({ editorState, tags, dirtyElements, dirtyLeaves }) => {
			// Our own accept (history-push) is quiet too: `rou|()` becomes `round|()`,
			// which would match again.
			// An accept that asked to reopen is worked out again, as an edit.
			const reopen = tags.has(REOPEN_TAG);
			const quiet = !reopen && (tags.has(HISTORY_PUSH_TAG) || quietRef.current.some(tag => tags.has(tag)));
			if (quiet || editor.isComposing()) { close(); return; }
			const edited = reopen || dirtyElements.size > 0 || dirtyLeaves.size > 0;
			const read = editorState.read(() => $textAndCaret(readRef.current));
			if (!read) { close(); return; }
			const env = { tokens: tokensRef.current };
			const found = suggestionAt(read.text, read.caret, env, providersRef.current);
			if (!found) { close(); return; }
			const previous = currentRef.current;
			// A caret move opens nothing; it only keeps a list that is still
			// about the same word.
			if (!edited && (!previous || previous.contextKey !== found.contextKey)) { close(); return; }
			// The same word with a caret move keeps the row the keys had reached.
			const keep = !edited && previous && previous.items.length === found.items.length ? Math.min(previous.active, found.items.length - 1) : 0;
			publish({ ...found, text: read.text, active: keep });
		});
	}, [editor, enabled, close, publish]);

	// The keys, only while the list is open.
	useEffect(() => {
		if (!enabled) return undefined;
		const move = step => event => {
			const current = currentRef.current;
			if (!current) return false;
			if (event) event.preventDefault();
			const count = current.items.length;
			setActive((current.active + step + count) % count);
			return true;
		};
		const take = event => {
			const current = currentRef.current;
			if (!current) return false;
			// A key that ends an IME composition is the IME's, not ours.
			if (event && (event.isComposing || event.keyCode === 229)) return false;
			if (event && event.shiftKey && event.key === 'Tab') return false;
			// A list that opened by itself in an empty argument (KPR-350: after
			// `(` or a picked value's comma) was not asked for: Tab leaves the
			// field as it always does, and only Enter takes a row there.
			if (event && event.key === 'Tab' && current.provider && current.provider.id === 'argument' && current.match && !current.match.query) {
				close();
				return false;
			}
			if (event) event.preventDefault();
			accept(current.active);
			return true;
		};
		const dismiss = event => {
			if (!currentRef.current) return false;
			if (event) {
				event.preventDefault();
				// Not the modal's: Escape closes the list first.
				event.stopPropagation();
			}
			close();
			return true;
		};
		const unregister = [
			editor.registerCommand(KEY_ARROW_DOWN_COMMAND, move(1), COMMAND_PRIORITY_CRITICAL),
			editor.registerCommand(KEY_ARROW_UP_COMMAND, move(-1), COMMAND_PRIORITY_CRITICAL),
			editor.registerCommand(KEY_ENTER_COMMAND, take, COMMAND_PRIORITY_CRITICAL),
			editor.registerCommand(KEY_TAB_COMMAND, take, COMMAND_PRIORITY_CRITICAL),
			editor.registerCommand(KEY_ESCAPE_COMMAND, dismiss, COMMAND_PRIORITY_CRITICAL),
			editor.registerCommand(BLUR_COMMAND, () => { close(); return false; }, COMMAND_PRIORITY_CRITICAL),
		];
		return () => unregister.forEach(fn => fn());
	}, [editor, enabled, accept, close, setActive]);

	const open = !!shown && enabled;
	return {
		open,
		items: open ? shown.items : [],
		more: open ? shown.more : 0,
		active: open ? shown.active : -1,
		listId,
		activeId: open ? optionId(shown.active) : undefined,
		optionId,
		accept,
		close,
	};
}
